import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { KioskCartPromoCodeBlockConfig } from '@front-platform/content-builder';
import { kioskCartPromoCodeBlockResolveConfig } from '@front-platform/content-builder';
import { Component, input } from '@angular/core';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { ICoupon } from '@core/models/cart.model';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { CartService } from '@core/services/cart/cart.service';
import { NotificationService } from '@core/services/notification/notification.service';
import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
import { TranslateService } from '@ngx-translate/core';
import { map, take, tap } from 'rxjs';
export interface KioskCartPromoCodeBlockRuntimeConfig {}

export type KioskCartPromoCodeBlockConfigInput = Partial<
  Omit<KioskCartPromoCodeBlockConfig, keyof KioskCartPromoCodeBlockRuntimeConfig> &
    KioskCartPromoCodeBlockRuntimeConfig
>;
export type KioskCartPromoCodeBlockResolvedConfig = Omit<
  KioskCartPromoCodeBlockConfig,
  keyof KioskCartPromoCodeBlockRuntimeConfig
> &
  Partial<KioskCartPromoCodeBlockRuntimeConfig>;
export interface KioskCartPromoCodeBlockContract extends ConfigComponentContract<
  KioskCartPromoCodeBlockConfigInput,
  KioskCartPromoCodeBlockResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-cart-promo-code-block',
  templateUrl: './kiosk-cart-promo-code-block.component.html',
  standalone: false,
})
export class KioskCartPromoCodeBlockComponent implements KioskCartPromoCodeBlockContract {
  readonly config = input<KioskCartPromoCodeBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskCartPromoCodeBlockConfig,
    KioskCartPromoCodeBlockRuntimeConfig
  >(kioskCartPromoCodeBlockResolveConfig, () => ({ config: this.config() }));
  get primaryButtonBackgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['primary_button_background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get primaryButtonTextColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['primary_button_text_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get secondaryButtonBackgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['secondary_button_background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get secondaryButtonTextColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['secondary_button_text_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get textColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor | undefined;
  }
  get backgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }

  protected order$ = this.orderSelector.selectValidation$;
  protected coupons$ = this.order$.pipe(map(order => order?.coupons as ICoupon[]));

  coupons = [];

  isValidating = false;
  errorVoucher: string | undefined = undefined;
  voucher: string | undefined = undefined;

  constructor(
    private readonly orderSelector: OrderStoreSelector,
    private readonly cartService: CartService,
    private readonly orderDispatcher: OrderStoreDispatcher,
    private readonly notificationService: NotificationService,
    private readonly translateService: TranslateService,
  ) {}

  applyVoucher(voucher: string | undefined): void {
    if (voucher) {
      voucher = voucher.trim();
      this.isValidating = true;
      this.cartService.addCoupon({ code: voucher });
      this.orderDispatcher
        .checkCartIsValid$()
        .pipe(
          take(1),
          tap(order => {
            const coupon = order?.coupons?.find(el => el.code === voucher);
            if (coupon?.error) {
              this.errorVoucher = coupon.error;
              this.isValidating = false;
              this.cartService.removeCoupon({ code: voucher });
              this.orderDispatcher.checkCartIsValid();
              return;
            }
            this.errorVoucher = '';
            this.notificationService.displayMessage(
              this.translateService.instant('basket.notification-valid-voucher'),
            );
            this.voucher = undefined;
          }),
        )
        .subscribe();
    }
  }

  removeCoupon(coupon: any): void {
    this.cartService.removeCoupon(coupon);
    this.orderDispatcher.checkCartIsValid();
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskCartPromoCodeBlockComponent, {
  'keyAliases': {},
});
